<!-- The game. Everything else in this app exists to get you here. -->
<template>
  <IonPage>
    <IonContent class="rm-content" :fullscreen="true">
      <div class="rm-page game">
        <header class="hud">
          <div>
            <span class="k">Score</span>
            <span class="v">{{ score.toLocaleString() }}</span>
          </div>
          <div class="mid">
            <span class="k">Pies left</span>
            <!-- Four dots read instantly; twenty-four do not. Past the handful
                 the board starts with, a number is the honest display. -->
            <span v-if="totalPies <= 4" class="pies">
              <i v-for="n in totalPies" :key="n" :class="{ off: n > pies }" />
            </span>
            <span v-else class="v pienum">{{ pies }}</span>
          </div>
          <div class="side">
            <!-- Not for a guest: the run belongs to the other phone, and a
                 button that appears to pause it but does not is worse than no
                 button at all. -->
            <button
              v-if="playable && !isGuest" type="button" class="pausebtn"
              :aria-label="state === 'paused' ? 'Resume' : 'Pause'"
              @click="state === 'paused' ? resume() : pause()"
            >
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                <path
                  d="M9 6 L9 18 M15 6 L15 18" fill="none" stroke="currentColor"
                  stroke-width="2.6" stroke-linecap="round"
                />
              </svg>
            </button>
            <span class="k">{{ names.nick }}</span>
            <span class="lives">
              <i v-for="n in Math.max(0, lives)" :key="n" />
              <em v-if="lives <= 0">—</em>
            </span>
          </div>
        </header>

        <div class="fright" :class="{ on: frightOn }" aria-hidden="true">
          <div class="fright-row">
            <span>{{ names.raf }} is running</span>
            <span>{{ frightLeft.toFixed(1) }}s</span>
          </div>
          <div class="bar"><span :style="{ width: frightPct + '%' }" /></div>
        </div>

        <div
          ref="board"
          class="board"
          @pointerdown="onDown"
          @pointerup="onUp"
          @pointercancel="swipe = null"
        >
          <canvas ref="cv" :aria-label="canvasLabel" role="img" />

          <div v-if="overlay" class="overlay" :class="overlay">
            <template v-if="overlay === 'joining'">
              <p class="big">{{ duelMessage }}</p>
              <p class="blurb">You are driving the ringed {{ names.raf }}.</p>
              <nav class="links">
                <button type="button" @click="endDuel()">Leave</button>
              </nav>
            </template>

            <template v-else-if="overlay === 'title'">
              <p class="mark">Pizza<span>Raf</span></p>
              <p class="tag">Eat the pizza. Outrun Raf.</p>


              <div class="setup">
                <div class="pills">
                  <span class="sk">Difficulty</span>
                  <div class="maps" role="radiogroup" aria-label="Difficulty">
                    <button
                      v-for="d in DIFFICULTIES"
                      :key="d.id"
                      type="button"
                      role="radio"
                      :aria-checked="difficulty === d.id"
                      :class="{ on: difficulty === d.id }"
                      @click="difficulty = d.id"
                    >{{ d.name }}</button>
                  </div>
                </div>
                <div class="stepper">
                  <span class="sk">{{ plural(names.raf) }}</span>
                  <button
                    type="button" aria-label="One fewer Raf"
                    :disabled="ghostCount <= 1" @click="setGhosts(ghostCount - 1)"
                  >&minus;</button>
                  <b>{{ ghostCount }}</b>
                  <button
                    type="button" aria-label="One more Raf"
                    :disabled="ghostCount >= MAX_GHOSTS" @click="setGhosts(ghostCount + 1)"
                  >+</button>
                </div>
                <div class="stepper">
                  <span class="sk">Extra pies</span>
                  <button
                    type="button" aria-label="One fewer pie"
                    :disabled="extraPies <= 0" @click="setExtraPies(extraPies - 1)"
                  >&minus;</button>
                  <b>{{ extraPies }}</b>
                  <button
                    type="button" aria-label="One more pie"
                    :disabled="extraPies >= MAX_EXTRA_PIES" @click="setExtraPies(extraPies + 1)"
                  >+</button>
                </div>
                <!-- Labelled, because two unlabelled rows of identical pills
                     read as one control with five options. -->
                <!-- Moved here from Settings: choosing who Nick and Raf
                     look like belongs next to the rest of the setup, on the
                     screen you are already on before a run. -->
                <div class="pills">
                  <span class="sk">Faces</span>
                  <div class="facepick">
                    <div v-for="f in FACE_SLOTS" :key="f.who" class="fp-col">
                      <span class="fp-head" :class="f.who">{{ f.role }}</span>
                      <label class="fp" @click="maybeNative(f.who, $event)">
                        <span class="fp-thumb" :class="{ empty: !faces[f.who] }">
                          <img v-if="faces[f.who]" :src="faces[f.who]" :alt="names[f.who]">
                          <svg
                            v-else-if="f.who === 'nick'" viewBox="-13 -13 26 26"
                            width="24" height="24" aria-hidden="true"
                          >
                            <circle cx="0" cy="2" r="8.8" fill="#ffd23f" />
                            <circle cx="-4.6" cy="-5.4" r="3.5" fill="#fff6e0" />
                            <circle cx="4.6" cy="-5.4" r="3.5" fill="#fff6e0" />
                            <circle cx="0" cy="-6.4" r="4.4" fill="#fff6e0" />
                            <rect x="-7.3" y="-7.2" width="14.6" height="3.9" fill="#fff6e0" />
                            <rect x="-7.6" y="-4.4" width="15.2" height="2.1" fill="#e8c96a" />
                            <circle cx="-3" cy="1" r="1.6" fill="#0b1020" />
                            <circle cx="3" cy="1" r="1.6" fill="#0b1020" />
                            <ellipse cx="0" cy="5.8" rx="2.6" ry="2.1" fill="#5a2410" />
                          </svg>
                          <svg v-else viewBox="-13 -13 26 26" width="24" height="24" aria-hidden="true">
                            <rect
                              x="-9.2" y="-9.2" width="18.4" height="18.4" rx="4.6"
                              fill="#ff2d95"
                            />
                            <circle cx="-3.5" cy="-1.6" r="2.9" fill="#fff" />
                            <circle cx="-2.7" cy="-1.6" r="1.4" fill="#0b1020" />
                            <circle cx="3.5" cy="-1.6" r="2.9" fill="#fff" />
                            <circle cx="4.3" cy="-1.6" r="1.4" fill="#0b1020" />
                          </svg>
                        </span>
                        <input
                          type="file" accept="image/png,image/jpeg,image/webp,image/gif"
                          @change="pickFace(f.who, $event)"
                        >
                      </label>
                      <input
                        class="fp-name" type="text" :maxlength="NAME_MAX"
                        :value="names[f.who]" :aria-label="`Name for ${f.label}`"
                        :placeholder="f.label"
                        @change="setName(f.who, $event.target.value)"
                        @keyup.enter="$event.target.blur()"
                      >

                      <div v-if="faces[f.who]" class="fp-size">
                        <input
                          type="range" :min="FACE_SCALE_MIN" :max="FACE_SCALE_MAX"
                          :step="FACE_SCALE_STEP" :value="faceScales[f.who]"
                          :aria-label="`${f.label} picture size`"
                          @input="setFaceScale(f.who, $event.target.value)"
                        >
                        <span>{{ Math.round(faceScales[f.who] * 100) }}%</span>
                        <button
                          type="button" class="fp-reset"
                          :disabled="faceScales[f.who] === FACE_SCALE_DEFAULT"
                          :aria-label="`Reset ${f.label} picture size`"
                          @click="setFaceScale(f.who, FACE_SCALE_DEFAULT)"
                        >
                          <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
                            <path
                              d="M4 12a8 8 0 1 0 2.3-5.7M4 4v4h4"
                              fill="none" stroke="currentColor" stroke-width="2.2"
                              stroke-linecap="round" stroke-linejoin="round"
                            />
                          </svg>
                        </button>
                      </div>
                      <button
                        v-if="faces[f.who]" type="button" class="fp-clear"
                        :aria-label="`Remove ${f.label}'s picture`"
                        @click="dropFace(f.who)"
                      >
                        <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                          <path
                            d="M7 7 L17 17 M17 7 L7 17" fill="none" stroke="currentColor"
                            stroke-width="2.6" stroke-linecap="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <p v-if="faceError" class="faceerr" role="alert">{{ faceError }}</p>

                <div class="pills">
                  <span class="sk">Pace</span>
                  <div class="maps" role="radiogroup" aria-label="Pace">
                    <button
                      v-for="p in SPEEDS" :key="p.id" type="button" role="radio"
                      :aria-checked="speed === p.id" :class="{ on: speed === p.id }"
                      @click="speed = p.id"
                    >{{ p.name }}</button>
                  </div>
                </div>
                <div class="pills">
                  <span class="sk">Map</span>
                  <div class="maps" role="radiogroup" aria-label="Map size">
                    <button
                      v-for="b in BOARDS" :key="b.id" type="button" role="radio"
                      :aria-checked="boardId === b.id" :class="{ on: boardId === b.id }"
                      @click="boardId = b.id"
                    >{{ b.name }}</button>
                  </div>
                </div>
              </div>
              <p v-if="bestFor(difficulty).score" class="best">
                Best {{ bestFor(difficulty).score.toLocaleString() }}
                · level {{ bestFor(difficulty).level }}
              </p>

              <button type="button" class="go" @click="play">Play</button>

              <!-- Two phones, same wifi. One drives Nick, the other drives one
                   Raf; the rest keep thinking for themselves. -->
              <div v-if="canDuel" class="duel">
                <span class="sk">Two players</span>
                <div v-if="duel.role === null" class="duel-start">
                  <button type="button" class="duel-host" @click="startHosting">Host a game</button>
                  <form class="joinform" @submit.prevent="startJoining">
                    <input
                      v-model="joinCode" type="text" inputmode="text"
                      autocapitalize="characters" autocomplete="off" autocorrect="off"
                      spellcheck="false" :maxlength="CODE_LEN" placeholder="CODE"
                      aria-label="Game code"
                    >
                    <button type="submit" :disabled="joinCode.length !== CODE_LEN">Join</button>
                  </form>
                  <p v-if="codeHint" class="codehint">{{ codeHint }}</p>
                </div>
                <div v-else class="duel-live">
                  <p v-if="duel.role === 'host' && duel.code" class="code">{{ duel.code }}</p>
                  <button
                    v-if="duel.role === 'host' && duel.code"
                    type="button" class="duel-share" @click="sendInvite"
                  >
                    Send the link
                  </button>
                  <p class="duel-status">{{ duelMessage }}</p>
                  <button type="button" class="duel-cancel" @click="endDuel()">Cancel</button>
                </div>
              </div>
              <p v-if="duel.error" class="faceerr" role="alert">{{ duel.error }}</p>
              <nav class="links">
                <button type="button" @click="router.push('/tabs/how')">How to play</button>
                <button type="button" @click="router.push('/tabs/settings')">Settings</button>
              </nav>
            </template>

            <template v-else-if="overlay === 'ready'">
              <p class="big">{{ level > 1 && freshLevel ? `Level ${level}` : 'Ready' }}</p>
            </template>

            <template v-else-if="overlay === 'paused'">
              <p class="big">Paused</p>
              <button type="button" class="go" @click="resume">Resume</button>
              <nav class="links">
                <button type="button" @click="quit">End run</button>
              </nav>
            </template>

            <template v-else-if="overlay === 'gameover'">
              <p class="big">{{ names.raf }} got you</p>
              <p class="final">{{ score.toLocaleString() }}</p>
              <p class="blurb">
                Level {{ level }} on {{ currentDifficulty().name }}<template v-if="beatBest">
                  · <b>new best</b></template>
              </p>
              <button type="button" class="go" @click="play">Play again</button>
              <nav class="links">
                <button type="button" @click="toTitle">Change difficulty</button>
              </nav>
            </template>
          </div>
        </div>

        <nav v-if="padOn && playable" class="pad" aria-label="Move Nick">
          <button type="button" class="up" aria-label="Up" @pointerdown.prevent="turn('up')">
            <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
              <path
                d="M6 15 L12 9 L18 15" fill="none" stroke="currentColor"
                stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"
              />
            </svg>
          </button>
          <button type="button" class="left" aria-label="Left" @pointerdown.prevent="turn('left')">
            <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
              <path
                d="M15 6 L9 12 L15 18" fill="none" stroke="currentColor"
                stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"
              />
            </svg>
          </button>
          <button type="button" class="right" aria-label="Right" @pointerdown.prevent="turn('right')">
            <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
              <path
                d="M9 6 L15 12 L9 18" fill="none" stroke="currentColor"
                stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"
              />
            </svg>
          </button>
          <button type="button" class="down" aria-label="Down" @pointerdown.prevent="turn('down')">
            <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
              <path
                d="M6 9 L12 15 L18 9" fill="none" stroke="currentColor"
                stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"
              />
            </svg>
          </button>
        </nav>
      </div>
    </IonContent>

    <FaceCropper
      v-if="cropping"
      :file="cropping.file"
      :label="cropping.label"
      @cancel="cropping = null"
      @error="onCropError"
      @done="onCropped"
    />
  </IonPage>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { IonPage, IonContent } from '@ionic/vue';
import { useRouter } from 'vue-router';
import {
  createGame, restart, steer, steerRaf, update, piesLeft, seedDemo,
} from '../game/engine.js';
import {
  host as hostDuel, join as joinDuel, supported as duelSupported,
  normaliseCode, hasAmbiguous, CODE_LEN,
} from '../game/duel.js';
import {
  encodeHello, encodeTick, encodeInput, decodeTick, foodOrder, boardFromHello, dirFromIndex,
} from '../game/netstate.js';
import { createBuffer } from '../game/remote.js';
import { inviteLink, inviteText, codeFromUrl, INVITE_TITLE } from '../game/invite.js';
import { shareLink } from '../core/export.js';
import FaceCropper from '../components/FaceCropper.vue';
import { createRenderer } from '../game/render.js';
import { DIRS } from '../game/maze.js';
import * as sfx from '../game/sfx.js';
import { haptic, toast } from '../core/util.js';
import { isDemo } from '../core/demo.js';
import {
  faces, loadFaces, saveFace, clearFace, reject as rejectFace,
} from '../game/faces.js';
import {
  DIFFICULTIES, BOARDS, SPEEDS, difficulty, padOn, currentDifficulty, bestFor, recordRun,
  boardId, ghostCount, extraPies, setGhosts, setExtraPies, setup, vegan,
  names, setName, plural, NAME_MAX,
  faceScales, setFaceScale, FACE_SCALE_MIN, FACE_SCALE_MAX, FACE_SCALE_STEP,
  FACE_SCALE_DEFAULT,
  scoreKey, speed, MAX_GHOSTS, MAX_EXTRA_PIES,
} from '../store.js';

const router = useRouter();
const cv = ref(null);
const board = ref(null);

// The game object is deliberately NOT reactive. It is mutated sixty times a
// second, and making Vue track that costs more than the game does — so the loop
// copies out only what the HUD renders.
let game = createGame(setup());
game.state = 'title';

// /demo opens on a run already under way rather than a title card, because a
// listing showing a menu tells nobody what the app is. `?flip` shows the
// moment after a fruit pie, which is the part worth seeing.
if (isDemo.value) {
  seedDemo(game, { flipped: new URLSearchParams(window.location.search).has('flip') });
}

const score = ref(0);
const lives = ref(3);
const level = ref(1);
const pies = ref(4);
const totalPies = computed(() => 4 + extraPies.value);
const state = ref('title');
const frightLeft = ref(0);
const frightPct = ref(0);
const freshLevel = ref(false);
const beatBest = ref(false);

const overlay = computed(() => {
  // A guest never sees the setup screen — it is not their game to set up — and
  // never a pause it did not ask for. Only "still connecting".
  if (isGuest.value) return streaming.value ? null : 'joining';
  return ['title', 'ready', 'paused', 'gameover'].includes(state.value) ? state.value : null;
});

const DUEL_WORDS = {
  opening: 'Opening a game…',
  waiting: 'Waiting for the other phone…',
  connecting: 'Connecting…',
  connected: 'Connected.',
};
const duelMessage = computed(() => {
  if (duel.value.role === 'host' && duel.value.status === 'waiting') {
    return 'Send the link, or read the code out. Waiting for them to join…';
  }
  return DUEL_WORDS[duel.value.status] ?? 'Connecting…';
});
const playable = computed(() => (
  isGuest.value ? streaming.value : !['title', 'gameover'].includes(state.value)
));
const frightOn = computed(() => state.value === 'playing' && frightLeft.value > 0);
const canvasLabel = computed(() => (
  state.value === 'playing'
    ? `Maze. Score ${score.value}. ${lives.value} lives left.`
    : 'PizzaRaf maze'
));

let renderer = null;
let raf = 0;
let last = 0;
let recorded = false;

/** Reads the local game, or a snapshot when this phone is the guest. */
function syncHud(src = game) {
  score.value = src.score;
  lives.value = src.lives;
  level.value = src.level;
  pies.value = piesLeft(src);
  state.value = src.state;
  frightLeft.value = Math.max(0, src.frightLeft);
  const span = game.diff.fright || 1;
  frightPct.value = Math.max(0, Math.min(100, (src.frightLeft / span) * 100));
}

/**
 * Whether anything is actually moving.
 *
 * On the title card, paused, or after a game over, the board is frozen behind
 * an overlay — and a requestAnimationFrame loop redrawing a static maze sixty
 * times a second is pure waste. It costs battery, and it is most of why
 * Lighthouse scored /app at 53: the first thing it measures is a screen where
 * nothing needs a single frame.
 */
const needsFrames = () => (
  // A guest has nothing to simulate but must keep drawing what arrives.
  isGuest.value || !['title', 'paused', 'gameover'].includes(game.state)
);

function loop(now) {
  // A backgrounded tab hands back a gap of seconds. Clamping is what stops
  // that gap being simulated all at once — which reads as Raf teleporting on
  // top of you the moment you come back.
  const dt = Math.min(0.05, (now - last) / 1000 || 0);
  last = now;

  // The guest simulates nothing. It draws the host's world, a tenth of a second
  // in the past, interpolated between the last two snapshots.
  if (isGuest.value) {
    const shown = view.sample(now);
    if (shown) { renderer?.draw(shown); syncHud(shown); }
    raf = requestAnimationFrame(loop);
    return;
  }

  if (game.state !== 'title') update(game, dt);

  for (const ev of game.events) {
    sfx.play(ev);
    if (ev === 'death' || ev === 'gameover') haptic('pop');
    if (ev === 'levelclear') freshLevel.value = true;
    if (ev === 'pizza') freshLevel.value = false;
  }
  game.events.length = 0;

  if (game.state === 'gameover' && !recorded) {
    recorded = true;
    beatBest.value = recordRun(scoreKey(game.diff.id), game.score, game.level);
  }

  renderer?.draw(game);
  syncHud();

  if (duel.value.role === 'host' && link && sendOrder && now - lastSent >= TICK_MS) {
    link.sendTick(encodeTick(game, sendOrder));
    lastSent = now;
  }

  // Scheduled at the END, so a state change made inside update() — running out
  // of lives, say — stops the loop on the same frame it happens.
  raf = needsFrames() ? requestAnimationFrame(loop) : 0;
}

/** Idempotent: starting an already-running loop must not double it. */
function start() {
  if (raf) return;
  last = window.performance.now();
  raf = requestAnimationFrame(loop);
}

function frame() {
  renderer?.draw(game);
  syncHud();
}

function play() {
  // A frozen loop has to be woken by whatever unfreezes it.
  restart(game);
  game.diff = currentDifficulty();
  recorded = false;
  beatBest.value = false;
  freshLevel.value = false;
  sfx.start();
  syncHud();
  start();
}

function toTitle() {
  game.state = 'title';
  frame();
}

function pause() {
  if (game.state !== 'playing' && game.state !== 'ready') return;
  game.resumeTo = game.state;
  game.state = 'paused';
  syncHud();
}

function resume() {
  // A frozen loop has to be woken by whatever unfreezes it.
  if (game.state !== 'paused') return;
  game.state = game.resumeTo ?? 'ready';
  syncHud();
  start();
}

function quit() {
  game.state = 'gameover';
  frame();
}

function turn(name) {
  if (isGuest.value) { link?.sendCtrl(encodeInput(DIRS[name])); return; }
  if (duel.value.role === 'host') { steer(game, DIRS[name]); return; }
  steer(game, DIRS[name]);
}

// --- input ------------------------------------------------------------------

const KEYS = {
  ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
  w: 'up', s: 'down', a: 'left', d: 'right',
  W: 'up', S: 'down', A: 'left', D: 'right',
};

/** Is the person typing into something, rather than playing? */
function isTyping(target) {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || target.isContentEditable;
}

function onKey(e) {
  // The movement keys are W, A, S and D, bound on the window and calling
  // preventDefault. That silently blocked those four letters from every text
  // field in the app — the game code box, and the character name fields.
  // A code containing any of them simply could not be typed, which is exactly
  // how "it won't take a fourth letter" was reported.
  if (isTyping(e.target)) return;

  if (KEYS[e.key]) {
    e.preventDefault();
    turn(KEYS[e.key]);
    return;
  }
  if (e.key === 'Escape' || e.key === 'p' || e.key === 'P') {
    e.preventDefault();
    if (game.state === 'paused') resume(); else pause();
  }
  if (e.key === 'Enter' || e.key === ' ') {
    if (game.state === 'title' || game.state === 'gameover') { e.preventDefault(); play(); }
  }
}

// --- faces -----------------------------------------------------------------

const FACE_SLOTS = [
  { who: 'nick', label: 'Nick', role: 'Chef' },
  { who: 'raf', label: 'Raf', role: 'Chaser' },
];
const faceError = ref('');

const cropping = ref(null);

/**
 * Choose a picture, then position it.
 *
 * On a native build the OS picker is used when it is there — it is the one the
 * phone's owner already knows, and on iOS and Android it offers its own crop.
 * Everywhere else it is a file input followed by the app's own cropper, which
 * is also the fallback when the native picker returns an uncropped image.
 */
async function pickFace(who, event) {
  const file = event.target.files?.[0];
  event.target.value = '';               // so choosing the same file again fires
  if (!file) return;
  faceError.value = '';
  const why = rejectFace(file);
  if (why) { faceError.value = why; return; }
  cropping.value = { who, file, label: names.value[who] || who };
}

/**
 * Prefer the phone's own picker when there is one.
 *
 * Falls through to the file input otherwise, which is what the label already
 * does — so this only ever intercepts on a native build.
 */
async function maybeNative(who, event) {
  if (!window.Capacitor?.Plugins?.Camera) return;
  event.preventDefault();
  faceError.value = '';
  await nativePick(who);
}

async function nativePick(who) {
  const camera = window.Capacitor?.Plugins?.Camera;
  if (!camera) return false;
  try {
    const photo = await camera.getPhoto({
      source: 'PHOTOS', resultType: 'dataUrl', allowEditing: true, quality: 90,
      width: 512, height: 512,
    });
    if (!photo?.dataUrl) return true;     // cancelled, but handled
    await saveFace(who, photo.dataUrl);
    return true;
  } catch {
    return true;                          // a cancelled native picker is not an error
  }
}

async function onCropped(dataUrl) {
  const who = cropping.value?.who;
  cropping.value = null;
  if (!who) return;
  try { await saveFace(who, dataUrl); }
  catch { faceError.value = 'That picture could not be saved.'; }
}

function onCropError(message) {
  cropping.value = null;
  faceError.value = message;
}

async function dropFace(who) {
  faceError.value = '';
  await clearFace(who);
}

// --- two players -----------------------------------------------------------
//
// The host simulates and sends snapshots; the guest sends one direction. Each
// phone uses its OWN uploaded faces rather than shipping images down the wire —
// a picture is far bigger than every snapshot of a whole game put together, and
// the characters are identifiable by position and colour anyway.

const TICK_MS = 33;                       // ~30 snapshots a second
const duel = ref({ role: null, status: 'idle', code: '', error: '' });
const rawCode = ref('');
const codeHint = ref('');

/**
 * The code field, normalised through a computed setter.
 *
 * `v-model` on a computed is the one shape where Vue owns the DOM value AND the
 * value can be cleaned on the way in. The previous version bound `:value` and
 * wrote back to `event.target.value` by hand, which raced Vue's re-render and
 * silently ate a keystroke — typing "6dbx" landed "6BX". That is exactly the
 * "it won't take a fourth letter" this was reported as.
 */
const joinCode = computed({
  get: () => rawCode.value,
  set(next) {
    rawCode.value = normaliseCode(next);
    // Say WHY a character vanished. Codes deliberately contain no O, 0, I or 1
    // so they survive being read aloud; somebody who misreads one and types it
    // otherwise just watches a key refuse to work.
    codeHint.value = hasAmbiguous(next)
      ? 'Codes never use O, 0, I or 1 — look again for Q, D, J or L.'
      : '';
  },
});
const isGuest = computed(() => duel.value.role === 'guest');
const canDuel = duelSupported();

let link = null;
let sendOrder = null;                     // host: the stable food ordering
let guest = null;                         // guest: { board, extraPies, order }
const view = createBuffer();
// The buffer is a plain object on purpose — it is written thirty times a second
// and Vue has no business tracking that. But anything the TEMPLATE decides from
// it needs a reactive signal, or a computed reading view.latest() caches its
// first answer and never re-runs. That cost an hour: the guest was receiving
// and drawing perfectly, behind an overlay that had cached "nothing yet".
const streaming = ref(false);
let lastSent = 0;

function endDuel(why = '') {
  link?.close();
  link = null;
  sendOrder = null;
  guest = null;
  view.reset();
  streaming.value = false;
  duel.value = { role: null, status: 'idle', code: '', error: why };
  // Give the Raf back to the computer. The flag survives into the next run via
  // restart(), and a Raf reserved for a player who has gone is one the AI skips
  // and nobody drives — it just stands in the maze.
  game.playerGhost = false;
  game.state = 'title';
  frame();
}

async function startHosting() {
  duel.value = { role: 'host', status: 'opening', code: '', error: '' };
  try {
    link = await hostDuel({
      onCode: (code) => { duel.value = { ...duel.value, code }; },
      onStatus: (status) => { duel.value = { ...duel.value, status }; },
      onCtrl: (msg) => {
        // The guest asks for the board when its channel opens, and keeps asking
        // until it gets one.
        if (msg?.t === 'hi') { link?.sendCtrl(encodeHello(game)); return; }
        if (msg?.t === 'in') steerRaf(game, dirFromIndex(msg.d));
      },
      onClosed: () => endDuel('The other player disconnected.'),
    });
    Object.assign(game, createGame({ ...setup(), playerGhost: true }));
    rebuildRenderer();
    sendOrder = foodOrder(game.board, game.extraPies);
    link.sendCtrl(encodeHello(game));
    recorded = false;
    beatBest.value = false;
    sfx.start();
    syncHud();
    start();
  } catch (e) {
    duel.value = { role: null, status: 'idle', code: '', error: e.message };
  }
}

/**
 * Hand the game to somebody who is not in the room.
 *
 * The share sheet where there is one, the clipboard where there is not — the
 * cascade in core/export.js, which is also the only thing that can tell a
 * dismissed sheet from a broken one. A dismissal is a decision, not a failure,
 * so it says nothing.
 */
async function sendInvite() {
  const code = duel.value.code;
  if (!code) return;
  const result = await shareLink(inviteLink(code), INVITE_TITLE, inviteText(code, names.value));
  // 'shared' speaks for itself and 'copied' already toasted on its way past.
  if (result === null) toast(`Could not share. The code is ${code}.`, { ms: 6000 });
}

async function startJoining() {
  const code = joinCode.value;
  if (code.length !== CODE_LEN) {
    duel.value = { ...duel.value, error: `A game code is ${CODE_LEN} characters — you have ${code.length}.` };
    return;
  }
  duel.value = { role: 'guest', status: 'connecting', code, error: '' };
  try {
    link = await joinDuel(code, {
      onStatus: (status) => { duel.value = { ...duel.value, status }; },
      onHello: (msg) => {
        const built = boardFromHello(msg);
        guest = { ...built, order: foodOrder(built.board, built.extraPies) };
        renderer = createRenderer(cv.value, built.board, { vegan: vegan.value });
        renderer.setFaces({ ...faces.value, nickScale: faceScales.value.nick, rafScale: faceScales.value.raf });
      },
      onTick: (msg) => {
        if (!guest) return;
        view.push(decodeTick(msg, guest.board, guest.extraPies, guest.order),
          window.performance.now());
        if (!streaming.value) streaming.value = true;
      },
      onClosed: () => endDuel('The host left.'),
    });
    start();
  } catch (e) {
    duel.value = { role: null, status: 'idle', code: '', error: e.message };
  }
}

const swipe = ref(null);
const onDown = (e) => { swipe.value = { x: e.clientX, y: e.clientY }; };

function onUp(e) {
  const from = swipe.value;
  swipe.value = null;
  if (!from) return;
  const dx = e.clientX - from.x;
  const dy = e.clientY - from.y;
  // Below this it is a tap, not a swipe, and a tap should not turn Nick into
  // a wall because your thumb moved four pixels.
  if (Math.abs(dx) < 22 && Math.abs(dy) < 22) return;
  turn(Math.abs(dx) > Math.abs(dy) ? (dx > 0 ? 'right' : 'left') : (dy > 0 ? 'down' : 'up'));
}

const onHide = () => { if (document.hidden) pause(); };

/** The renderer is sized from the board, so a different board is a new one. */
function rebuildRenderer() {
  if (!cv.value) return;
  renderer = createRenderer(cv.value, game.board, { vegan: vegan.value });
  renderer.setFaces({ ...faces.value, nickScale: faceScales.value.nick, rafScale: faceScales.value.raf });
}

// Between runs, keep the previewed board in step with the setting so the title
// screen shows the maze you are about to play.
watch([difficulty, boardId, ghostCount, extraPies, speed, vegan], () => {
  if (game.state !== 'title' && game.state !== 'gameover') return;
  // `setup()` has no playerGhost, so building from it alone quietly demoted the
  // guest's Raf back to an ordinary one. Between two runs of a hosted game —
  // exactly when someone reaches for these settings — the second player lost
  // their character and spent the whole round watching.
  Object.assign(game, createGame({ ...setup(), playerGhost: game.playerGhost }));
  game.state = 'title';
  rebuildRenderer();
  // The guest is drawing a board it never built, from the hello it was sent.
  // Every setting here changes that board, its food, or how many Rafs are on
  // it, so a stale hello leaves them decoding the previous game.
  if (link && duel.value.role === 'host') {
    sendOrder = foodOrder(game.board, game.extraPies);
    link.sendCtrl(encodeHello(game));
  }
  frame();
});


// Uploaded faces live on this device; loading them is a database read, so the
// game starts on the drawn characters and swaps when they arrive.
watch(vegan, () => { rebuildRenderer(); frame(); });

watch([faces, faceScales], () => {
  renderer?.setFaces({
    ...faces.value, nickScale: faceScales.value.nick, rafScale: faceScales.value.raf,
  });
  if (!raf) setTimeout(frame, 60);
}, { deep: true });

onMounted(async () => {
  // After the first paint, not during it. Building the maze layer and the
  // sprite sheet is a few megapixels of canvas work, and doing it inline makes
  // the browser wait for it before showing anything at all.
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  rebuildRenderer();
  loadFaces().then(() => {
    renderer?.setFaces({
      ...faces.value, nickScale: faceScales.value.nick, rafScale: faceScales.value.raf,
    });
    setTimeout(frame, 60);
  }).catch(() => {});
  syncHud();
  window.addEventListener('keydown', onKey);
  document.addEventListener('visibilitychange', onHide);
  frame();
  // Only starts if something is actually moving — on the title card it does not.
  start();

  // Arrived on a join link. Going straight in is the whole point of the link:
  // the code is already known, and stopping to make them press Join is asking
  // them to confirm the thing they just tapped.
  //
  // The parameter is dropped afterwards so a reload does not retry a room that
  // has since been used up — the second attempt fails with "somebody already
  // joined that game", which reads as a bug rather than as a stale tab.
  const invited = codeFromUrl(window.location.href);
  if (invited && canDuel) {
    window.history.replaceState({}, '', window.location.pathname);
    joinCode.value = invited;
    startJoining();
  }
});

onBeforeUnmount(() => {
  link?.close();
  cancelAnimationFrame(raf);
  window.removeEventListener('keydown', onKey);
  document.removeEventListener('visibilitychange', onHide);
});
</script>

<style scoped>
.game {
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* `height`, not `min-height`: the column has to be BOUNDED for `flex: 1` on
     the board to mean "the space that is left" rather than "as much as I want".
     With min-height the canvas grew to the viewport width on a desktop, made
     itself taller than the window from its own aspect ratio, and ran off the
     bottom of the screen. */
  height: 100%;
  /* A phone-shaped column, centred. A maze this tall has nothing to do with the
     width of a laptop, and stretched across one the HUD ends up in three far
     corners with the board marooned in the middle. */
  max-width: 460px;
  margin: 0 auto;
  padding: max(14px, env(safe-area-inset-top)) 18px max(14px, env(safe-area-inset-bottom));
  background: var(--rm-bg);
}

/* --- HUD --- */
.hud { display: flex; justify-content: space-between; align-items: flex-end; }
.hud .mid { text-align: center; }
.hud .side { text-align: right; }
.k { font-size: 10px; font-weight: 500; color: var(--rm-dim); display: block; margin-bottom: 5px; }
.v { font-size: 25px; font-weight: 700; letter-spacing: -0.02em; line-height: 1; }
.pies { display: flex; gap: 5px; justify-content: center; padding-bottom: 5px; }
.pies i {
  width: 9px; height: 9px; border-radius: 50%; display: block;
  background: var(--rm-pink); box-shadow: 0 1px 7px rgb(255 45 149 / 55%);
}
.pies i.off { background: #1d2947; box-shadow: none; }
.pienum { color: var(--rm-pink); font-size: 21px; }
.lives { display: flex; gap: 6px; justify-content: flex-end; padding-bottom: 4px; min-height: 18px; }
/* A life is a chef's head. It used to be a disc with a wedge clipped out of
   it, which was the same registered silhouette as the character — and at the
   top of the screen at 16px it was the most legible copy of it in the app. */
.lives i {
  width: 16px; height: 16px; border-radius: 50%; display: block; position: relative;
  background: var(--rm-gold); box-shadow: 0 1px 7px rgb(255 210 63 / 45%);
}
.lives i::before {
  content: ''; position: absolute; left: 50%; top: -3px;
  width: 13px; height: 8px; transform: translateX(-50%);
  border-radius: 6px 6px 2px 2px; background: #fff6e0;
}
.lives em { color: var(--rm-dim); font-style: normal; font-size: 13px; }

/* --- the frightened timer --- */
.fright { opacity: 0; transition: opacity 200ms ease-out; }
.fright.on { opacity: 1; }
.fright-row {
  display: flex; justify-content: space-between;
  font-size: 11px; font-weight: 700; color: var(--rm-gold); margin-bottom: 6px;
}
.bar { height: 5px; border-radius: 3px; background: #18213c; overflow: hidden; }
.bar span {
  display: block; height: 100%; border-radius: 3px;
  background: linear-gradient(90deg, var(--rm-pink), var(--rm-gold));
  transition: width 90ms linear;
}

/* --- board --- */
.board {
  position: relative; flex: 1; min-height: 0; touch-action: none;
  display: grid; place-items: center;
}
.board canvas {
  /* The canvas has a real intrinsic size — its backing store — so bounding it
     on both axes and leaving width and height auto scales it down to fit while
     keeping its ratio. No object-fit, and so no letterboxing inside an element
     that is the wrong shape. */
  display: block;
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
}

/* --- overlays --- */
.overlay {
  position: absolute; inset: 0; display: flex; flex-direction: column;
  align-items: center; gap: 12px; text-align: center;
  /* Start at the top rather than centring. Centred in a board this tall, the
     wordmark sat a long way down the screen with nothing above it. Short
     overlays (Ready, Paused) still centre — see below. */
  justify-content: flex-start;
  padding: 10px 18px 18px; background: rgb(8 11 22 / 92%); overflow-y: auto;
  animation: rise 260ms cubic-bezier(0.16, 1, 0.3, 1);
}
@keyframes rise { from { opacity: 0; transform: translateY(10px); } }
@media (prefers-reduced-motion: reduce) { .overlay { animation: none; } }

.overlay.ready, .overlay.paused, .overlay.gameover, .overlay.joining {
  justify-content: center;
}

.mark {
  margin: 0; font-size: 40px; font-weight: 700; letter-spacing: -0.04em; line-height: 1;
  color: var(--rm-gold); text-shadow: 0 0 26px rgb(255 210 63 / 35%);
}
.mark span { color: var(--rm-pink); text-shadow: 0 0 26px rgb(255 45 149 / 40%); }
.tag { margin: -6px 0 4px; font-size: 13px; color: var(--rm-dim); }
.big { margin: 0; font-size: 30px; font-weight: 700; letter-spacing: -0.03em; }
.final {
  margin: -8px 0 0; font-size: 46px; font-weight: 700;
  letter-spacing: -0.03em; color: var(--rm-gold); line-height: 1;
}
.blurb { margin: 0; font-size: 12px; line-height: 1.5; color: var(--rm-dim); max-width: 27ch; }
.blurb b { color: var(--rm-gold); }
.best { margin: -6px 0 0; font-size: 11px; color: var(--rm-cyan); }

.picker { display: flex; gap: 6px; }
/* Aligned with the labelled rows below it, so Difficulty, Faces, Pace and Map
   all read as the same kind of control. */
.picker button {
  font: inherit; font-size: 12px; font-weight: 500; padding: 8px 13px;
  color: var(--rm-dim); background: var(--rm-panel);
  border: 1px solid var(--rm-edge); border-radius: 8px; cursor: pointer;
  transition: color 140ms, border-color 140ms, background 140ms;
}
.picker button.on { color: var(--rm-bg); background: var(--rm-cyan); border-color: var(--rm-cyan); }

.setup { display: flex; flex-direction: column; gap: 7px; width: 100%; max-width: 280px; }
.stepper {
  display: grid; grid-template-columns: 1fr 34px 40px 34px; align-items: center; gap: 6px;
  padding: 5px 5px 5px 12px;
  background: var(--rm-panel); border: 1px solid var(--rm-edge); border-radius: 9px;
}
.sk { font-size: 12px; color: var(--rm-dim); text-align: left; }
.stepper b { font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; }
.stepper button {
  font: inherit; font-size: 17px; line-height: 1; height: 30px; cursor: pointer;
  color: var(--rm-ink); background: #16203c;
  border: 1px solid var(--rm-edge); border-radius: 7px;
}
.stepper button:disabled { opacity: 0.3; cursor: default; }
.stepper button:not(:disabled):active { background: var(--rm-cyan); color: var(--rm-bg); }

.pills { display: flex; flex-direction: column; gap: 5px; align-items: flex-start; width: 100%; }
.pills .sk { font-size: 11px; padding-left: 2px; }
.maps { display: flex; gap: 6px; width: 100%; }

.facepick { display: flex; gap: 8px; align-items: flex-start; width: 100%; }
.fp-col {
  position: relative;
  flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 5px;
  padding: 7px; background: var(--rm-panel);
  border: 1px solid var(--rm-edge); border-radius: 10px;
}
.fp { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.fp-size { display: flex; align-items: center; gap: 7px; }
.fp-size span {
  font-size: 10px; color: var(--rm-dim); min-width: 28px; text-align: right;
  font-variant-numeric: tabular-nums;
}
.fp-size input[type='range'] {
  flex: 1; min-width: 0; appearance: none; height: 3px; border-radius: 2px;
  background: var(--rm-edge); cursor: pointer;
}
.fp-size input[type='range']::-webkit-slider-thumb {
  appearance: none; width: 15px; height: 15px; border-radius: 50%;
  background: var(--rm-cyan); border: 0; cursor: pointer;
}
.fp-reset {
  display: grid; place-items: center; flex: none; width: 20px; height: 20px; padding: 0;
  cursor: pointer; color: var(--rm-cyan); background: none;
  border: 1px solid var(--rm-edge); border-radius: 6px;
}
.fp-reset:disabled { opacity: 0.25; cursor: default; }
.fp input { display: none; }
.fp:focus-within { outline: 2px solid var(--rm-cyan); outline-offset: 2px; }
.fp-thumb {
  width: 30px; height: 30px; flex: none; border-radius: 50%; overflow: hidden;
  display: grid; place-items: center; background: #0a1122;
  border: 1px solid var(--rm-edge); color: var(--rm-dim);
}
.fp-thumb img { width: 100%; height: 100%; object-fit: cover; display: block; }
.fp-name {
  font: inherit; font-size: 12px; width: 100%; min-width: 0; padding: 3px 6px;
  color: var(--rm-ink); background: #0a1122;
  border: 1px solid transparent; border-radius: 6px;
}
.fp-name:hover { border-color: var(--rm-edge); }
.fp-name:focus { border-color: var(--rm-cyan); outline: none; }
.fp-name::placeholder { color: var(--rm-dim); }
.fp-head {
  /* Which character this card IS, above the field for what you call them. In
     the character's own colour, so it matches what you see on the board. */
  font-size: 10px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
  text-align: left; padding-left: 2px; margin-bottom: 1px;
}
.fp-head.nick { color: var(--rm-gold); }
.fp-head.raf { color: var(--rm-pink); }
.fp-clear {
  position: absolute; top: 4px; right: 4px; z-index: 1;
  display: grid; place-items: center; width: 19px; height: 19px; padding: 0;
  cursor: pointer; color: var(--rm-dim); background: #0a1122;
  border: 1px solid var(--rm-edge); border-radius: 50%;
}
.fp-clear:active { color: var(--rm-pink); border-color: var(--rm-pink); }
.faceerr {
  font-size: 11px; line-height: 1.45; color: var(--rm-pink); margin: 0;
  max-width: 280px;
}

.duel {
  display: flex; flex-direction: column; gap: 7px; align-items: center;
  width: 100%; max-width: 280px; margin-top: 4px;
  padding-top: 14px; border-top: 1px solid var(--rm-edge);
}
/* Two rows, not one. Sharing a 280px row with "Host a game" left the code
   field 67px wide with 45px of usable space — and four bold, letter-spaced
   characters need 43px of it. On any phone with a wider fallback font or a
   larger text setting the fourth character scrolled out of sight, which reads
   exactly like a field that will not accept it. */
.duel-start { display: flex; flex-direction: column; gap: 7px; width: 100%; }
.duel-host { width: 100%; }
.duel-host, .joinform button {
  font: inherit; font-size: 12px; padding: 9px 12px; cursor: pointer;
  color: var(--rm-ink); background: var(--rm-panel);
  border: 1px solid var(--rm-edge); border-radius: 9px;
}
.joinform button:disabled { opacity: 0.4; }
.codehint { margin: 0; font-size: 10px; line-height: 1.45; color: var(--rm-gold); max-width: 30ch; }
.joinform { display: flex; gap: 6px; width: 100%; }
.joinform input {
  font: inherit; font-size: 16px; font-weight: 700; letter-spacing: 0.22em;
  flex: 1; min-width: 0; padding: 9px 10px; text-align: center; text-transform: uppercase;
  color: var(--rm-ink); background: #0a1122;
  border: 1px solid var(--rm-edge); border-radius: 9px;
}
.joinform input::placeholder { color: var(--rm-dim); letter-spacing: 0.14em; font-weight: 400; }
.duel-live { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.code {
  margin: 0; font-size: 34px; font-weight: 700; letter-spacing: 0.16em;
  color: var(--rm-cyan); text-shadow: 0 0 24px rgb(47 212 238 / 35%);
}
.duel-status { margin: 0; font-size: 12px; color: var(--rm-dim); max-width: 26ch; }
.duel-share {
  font: inherit; font-size: 13px; font-weight: 700; padding: 9px 20px;
  cursor: pointer; color: var(--rm-bg); background: var(--rm-cyan);
  border: 0; border-radius: 9px;
}
.duel-share:active { opacity: 0.85; }
.duel-cancel {
  font: inherit; font-size: 11px; color: var(--rm-dim); background: none;
  border: 0; padding: 3px 6px; cursor: pointer;
  text-decoration: underline; text-underline-offset: 3px;
}
.maps button {
  flex: 1; font: inherit; font-size: 12px; padding: 8px 0; cursor: pointer;
  color: var(--rm-dim); background: var(--rm-panel);
  border: 1px solid var(--rm-edge); border-radius: 9px;
}
.maps button.on { color: var(--rm-bg); background: var(--rm-gold); border-color: var(--rm-gold); }

.go {
  font: inherit; font-size: 16px; font-weight: 700; letter-spacing: 0.01em;
  padding: 13px 40px; margin-top: 4px; cursor: pointer;
  color: var(--rm-bg); background: var(--rm-pink);
  border: 0; border-radius: 10px;
  box-shadow: 0 6px 22px rgb(255 45 149 / 35%);
}
.go:active { transform: translateY(1px); }

.links { display: flex; gap: 16px; }
.links button {
  font: inherit; font-size: 12px; color: var(--rm-dim);
  background: none; border: 0; padding: 4px; cursor: pointer;
  text-decoration: underline; text-underline-offset: 3px;
}

.overlay :focus-visible, .pad :focus-visible {
  outline: 2px solid var(--rm-cyan); outline-offset: 3px;
}

/* --- d-pad --- */
.pad {
  /* Pause has moved to the HUD. It sat one thumb-width from Down, which is a
     button you press by accident in the middle of a chase — and the one button
     whose whole job is to interrupt you. */
  display: grid; gap: 12px; justify-content: center; flex: none; margin-top: auto;
  grid-template-columns: repeat(3, 76px);
  grid-template-rows: repeat(2, 62px);
  grid-template-areas: '. up .' 'left down right';
}
.pad button {
  display: grid; place-items: center; padding: 0;
  color: var(--rm-ink); background: var(--rm-panel);
  border: 1px solid var(--rm-edge); border-radius: 13px; cursor: pointer;
  transition: background 90ms, border-color 90ms;
}
.pad button:active { background: #16203c; border-color: var(--rm-cyan); color: var(--rm-cyan); }
.up { grid-area: up; }
.down { grid-area: down; }
.left { grid-area: left; }
.right { grid-area: right; }

.pausebtn {
  display: inline-grid; place-items: center; width: 30px; height: 30px;
  margin-bottom: 4px; padding: 0; cursor: pointer;
  color: var(--rm-dim); background: var(--rm-panel);
  border: 1px solid var(--rm-edge); border-radius: 8px;
}
.pausebtn:active { color: var(--rm-cyan); border-color: var(--rm-cyan); }
.hud .side { display: flex; flex-direction: column; align-items: flex-end; }
</style>
